/**
 * 🚀 ADD ELEMENT BUTTON 2025
 * 
 * Plus-Icon zwischen Elementen zum Hinzufügen neuer Elemente
 * - Nutzt die wiederverwendbare AddElementMenu Komponente
 * - Portal-Rendering für perfektes Positioning
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import AddElementMenu from './AddElementMenu';
import './AddElementButton.css';

function AddElementButton({ onAdd, position = 'between' }) {
    const [showMenu, setShowMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState(null);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    // ✅ Calculate menu position when opening
    useEffect(() => {
        if (showMenu && buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const menuHeight = 400; // Estimated max height (50vh ≈ 400px)
            const menuWidth = 220;
            
            // Calculate horizontal position (centered)
            let left = buttonRect.left + (buttonRect.width / 2) - (menuWidth / 2);
            
            // Prevent off-screen horizontally
            if (left < 10) left = 10;
            if (left + menuWidth > window.innerWidth - 10) {
                left = window.innerWidth - menuWidth - 10;
            }
            
            // ✅ Smart vertical positioning: Check if space above
            const spaceAbove = buttonRect.top;
            const spaceBelow = window.innerHeight - buttonRect.bottom;
            
            let top;
            let direction;
            
            if (spaceAbove >= menuHeight || spaceAbove > spaceBelow) {
                // ✅ Genug Platz oben ODER mehr Platz oben als unten → nach oben
                top = buttonRect.top - 8;
                direction = 'up';
            } else {
                // ✅ Mehr Platz unten → nach unten
                top = buttonRect.bottom + 8;
                direction = 'down';
            }
            
            setMenuPosition({ top, left, direction });
        }
    }, [showMenu]);

    // ✅ Close menu on outside click (kein Delay mehr nötig)
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is inside menu OR button
            const clickedInMenu = menuRef.current && menuRef.current.contains(event.target);
            const clickedInButton = buttonRef.current && buttonRef.current.contains(event.target);
            
            if (!clickedInMenu && !clickedInButton) {
                setShowMenu(false); // ✅ Sofort schließen
            }
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showMenu]);

    const handleAddElement = (type, elementType = 'custom') => {
        onAdd(type, elementType);
        setShowMenu(false);
        setMenuPosition(null); // ✅ Reset position
    };
    
    const handleCloseMenu = () => {
        setShowMenu(false);
        setMenuPosition(null);
    };
    
    // ✅ Reset position when menu closes
    useEffect(() => {
        if (!showMenu) {
            setMenuPosition(null);
        }
    }, [showMenu]);

    return (
        <div className="add-element-wrapper" data-position={position}>
            <button
                ref={buttonRef}
                className="add-element-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(!showMenu); // ✅ Einfach: Toggle on Click
                }}
                title="Add Element"
            >
                <i className="fas fa-plus"></i>
            </button>
            
            {/* ✅ Nutze die wiederverwendbare AddElementMenu Komponente */}
            {showMenu && menuPosition && ReactDOM.createPortal(
                <div 
                    ref={menuRef}
                    style={{
                        position: 'fixed',
                        top: menuPosition.direction === 'up' 
                            ? 'auto' 
                            : `${menuPosition.top}px`,
                        bottom: menuPosition.direction === 'up' 
                            ? `${window.innerHeight - menuPosition.top}px` 
                            : 'auto',
                        left: `${menuPosition.left}px`,
                        zIndex: 9999999
                    }}
                >
                    <AddElementMenu 
                        onAdd={handleAddElement}
                        onClose={handleCloseMenu}
                    />
                </div>,
                document.body
            )}
        </div>
    );
}

export default AddElementButton;

